import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { Warband } from '../../types';
import { warbandService } from '../../services/warbandService';
import { useWarbandStore } from '../../stores/warbandStore';

interface ManageGoldModalProps {
  open: boolean;
  warband: Warband;
  onClose: () => void;
  title?: string;
  label?: string;
  field?: 'gold' | 'experience';
}

export const ManageGoldModal: React.FC<ManageGoldModalProps> = ({ open, warband, onClose, title = 'Manage Gold', label = 'Gold (gc)', field = 'gold' }) => {
  const { setWarbands, warbands } = useWarbandStore();
  const [value, setValue] = React.useState<number>(warband[field]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setValue(warband[field]);
      setError(null);
    }
  }, [open, warband, field]);

  const handleSave = async () => {
    if (value < 0) {
      setError('Value cannot be negative.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await warbandService.update(warband.id, { ...warband, [field]: value });
      setWarbands(warbands.map((w) => (w.id === warband.id ? { ...w, [field]: value } : w)));
      onClose();
    } catch {
      setError('Failed to update value. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          label={label}
          type="number"
          value={value}
          onChange={(e) => setValue(Math.max(0, parseInt(e.target.value, 10) || 0))}
          fullWidth
          autoFocus
          sx={{ mt: 1 }}
          slotProps={{ htmlInput: { min: 0 } }}
          error={!!error}
          helperText={error ?? undefined}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};
